import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSEO } from "../lib/seo";
import { fadeInUp } from "../lib/brand";
import { SERVICES, getService } from "../data/services";
import { FRAMEWORK } from "../data/framework";
import { SectionHeading, PrimaryLink } from "../components/site/Primitives";
import { PageHero, CTASection } from "../components/site/Sections";
import { FrameworkSection } from "../components/site/FrameworkGraphic";

const stagger = (i) => ({ ...fadeInUp, transition: { ...fadeInUp.transition, delay: 0.06 * i } });

const Offerings = ({ service }) => (
  <section data-testid="service-offerings" className="py-24 md:py-32 bg-summit-sand">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHeading eyebrow={service.offeringsHeading} title={`${service.shortTitle} services`} className="mb-14" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-summit-border">
        {service.offerings.map((o, i) => (
          <motion.div key={o.title} {...stagger(i)} data-testid={`offering-${i}`} className="border-b border-r border-summit-border p-8 lg:p-9 hover:bg-white transition-colors">
            <span className="font-heading text-3xl text-summit-gold-dark leading-none">{String(i + 1).padStart(2, "0")}</span>
            <h3 className="mt-5 font-heading text-2xl font-medium text-summit-charcoal leading-tight">{o.title}</h3>
            <p className="mt-3 font-body text-sm text-summit-text-muted leading-relaxed">{o.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Related = ({ current }) => (
  <section data-testid="related-services" className="py-20 bg-summit-bg border-t border-summit-border">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <p className="font-body text-xs uppercase tracking-[0.22em] text-summit-gold-dark font-semibold mb-8">Related Services</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {SERVICES.filter((s) => s.slug !== current).map((s) => (
          <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`related-${s.slug}`} className="group border border-summit-border p-6 card-lift bg-white">
            <h3 className="font-heading text-2xl text-summit-charcoal group-hover:text-summit-gold-dark transition-colors">{s.title}</h3>
            <p className="mt-2 font-body text-sm text-summit-text-muted">{s.summary}</p>
            <span className="mt-4 inline-flex items-center gap-2 font-body text-sm font-semibold text-summit-forest">Explore {s.shortTitle} <ArrowRight size={14} /></span>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default function ServicePage() {
  const { slug } = useParams();
  const service = getService(slug);
  useSEO(service?.seoTitle, service?.seoDescription);
  if (!service) return <Navigate to="/" replace />;

  return (
    <>
      <PageHero eyebrow={service.badge || "Service"} title={service.title} copy={service.heroStatement} testId="service-hero">
        <p className="mt-5 font-body text-base text-summit-text-muted leading-relaxed max-w-2xl">{service.heroCopy}</p>
        <div className="mt-8">
          <PrimaryLink to="/contact" testId="service-hero-cta">{service.ctaLabel}</PrimaryLink>
        </div>
      </PageHero>

      <section data-testid="service-intro" className="py-24 md:py-32 bg-summit-bg border-t border-summit-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeading title={service.intro.heading} />
          </div>
          <motion.div {...stagger(2)} className="lg:col-span-6 lg:col-start-7">
            <p className="font-heading text-2xl md:text-3xl text-summit-charcoal leading-snug">{service.intro.statement}</p>
            <p className="mt-6 font-body text-base text-summit-text-muted leading-relaxed">{service.intro.paragraph}</p>
            <p className="mt-6 font-body text-xs uppercase tracking-[0.2em] text-summit-gold-dark font-semibold">Where AI Strategy Meets Organizational Transformation</p>
          </motion.div>
        </div>
      </section>

      <Offerings service={service} />

      {service.showFramework && (
        <section data-testid="service-framework" className="py-24 md:py-32 bg-white border-y border-summit-border">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
              <div className="lg:col-span-7">
                <SectionHeading eyebrow="Proprietary Methodology" title={FRAMEWORK.name} copy={FRAMEWORK.tagline} />
              </div>
              <motion.p {...stagger(2)} className="lg:col-span-5 lg:pt-14 font-heading text-2xl md:text-3xl text-summit-charcoal leading-snug">
                The methodology behind every Summit engagement—from strategy to sustainable transformation.
              </motion.p>
            </div>
            <FrameworkSection />
          </div>
        </section>
      )}

      <CTASection heading={service.ctaHeading} primaryLabel={service.ctaLabel} testId="service-cta" />
      <Related current={service.slug} />
    </>
  );
}
